import useTranslation from 'next-translate/useTranslation'
import SearchIcon from '@ancon/wildcat-ui/shared/icons/i-search.svg'
import { useEffect, useRef, useState } from 'react'
import {
  CustomerAddress,
  OutletAddress,
  CustomerDeliveryAddress,
  CustomerAddressType,
  OrderDelivery,
} from '@ancon/wildcat-types'
import BackIcon from '@ancon/wildcat-ui/shared/icons/arrow-back-long.svg'
import { isCoordinateWithinBounds } from '@ancon/wildcat-utils/location'
import getCustomerAddressFromGeocoderResult from '@ancon/wildcat-utils/user/getCustomerAddressFromGeocoderResult'
import { useRouter } from 'next/router'
import { IMapProps } from 'google-maps-react'

import Modal from '../../../app/components/modal/Modal'
import Input from '../../../app/components/input/Input'
import {
  outletDetailsDeliveryAreaSelector,
  outletDetailsIdASelector,
  outletIsOutletDetailsPendingSelector,
  outletIsVisibleDeliveryLocationModalSelector,
  outletListItemSelector,
  outletSelectedOutletAddressSelector,
} from '../../../outlet/store/outletSelector'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import PlacesSuggestions from '../searchOutlets/suggestions/PlacesSuggestions'
import { ScriptStatus, useScript } from '../../../app/hooks/useScript'
import {
  AppTestIds,
  GoogleMapsAPIUrl,
  StockholmCoordinates,
} from '../../../app/constants'
import Button from '../../../app/components/Button'
import useGeocodeService from '../../../location/hooks/useGeocodeService'
import getCheckoutDeliveryDetails from '../../../checkout/utils/getCheckoutDeliveryDetailsFromCustomerDetails'
import { clientContextCustomerSelector } from '../../../clientContext/store/clientContextSelectors'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import { fetchOutletDetails } from '../../../outlet/store/outletThunks'
import { checkoutCurrentCheckoutOutletListItemSelector } from '../../../checkout/store/checkoutSelectors'
import { outletSetIsVisibleDeliveryLocationModal } from '../../../outlet/store/outletSlice'
import MapView from '../../../app/components/MapView'

import DeliveryLocationConfigureModalSkeleton from './DeliveryLocationConfigureModalSkeleton'
import styles from './DeliveryLocationConfigureModal.module.scss'

type CustomerDeliveryAddressWithNote = CustomerDeliveryAddress & {
  note?: string
}

function getInitialDeliveryAddress(
  initialAddress?: CustomerAddress,
  outletAddress?: OutletAddress,
): CustomerDeliveryAddress {
  const defaultAddress: CustomerDeliveryAddressWithNote = {
    street: '',
    zipCode: '',
    city: '',
    state: '',
    country: '',
    location: {
      latitude: StockholmCoordinates.latitude,
      longitude: StockholmCoordinates.longitude,
    },
  }

  return (
    initialAddress || {
      ...defaultAddress,
      ...outletAddress,
      location: outletAddress
        ? outletAddress.location
        : {
            latitude: StockholmCoordinates.latitude,
            longitude: StockholmCoordinates.longitude,
          },
    }
  )
}

type DeliveryLocationConfigureModalProps = {
  initialAddress?: CustomerAddress
  isCheckoutPage?: boolean
  onClickContinue: (orderDelivery?: OrderDelivery) => void
}

export default function DeliveryLocationConfigureModal({
  initialAddress,
  isCheckoutPage = false,
  onClickContinue,
}: DeliveryLocationConfigureModalProps) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation('common')

  const router = useRouter()
  const selectedOutletAddress = useAppSelector(
    outletSelectedOutletAddressSelector,
  )
  const checkoutOutletListItem = useAppSelector(
    checkoutCurrentCheckoutOutletListItemSelector,
  )
  const outletListItem = useAppSelector(outletListItemSelector)
  const outlet = isCheckoutPage ? checkoutOutletListItem : outletListItem
  const outletDetailsId = useAppSelector(outletDetailsIdASelector)
  const outletDeliveryArea = useAppSelector(outletDetailsDeliveryAreaSelector)
  const isOutletDetailsPending = useAppSelector(
    outletIsOutletDetailsPendingSelector,
  )
  const customer = useAppSelector(clientContextCustomerSelector)
  const isOpen = useAppSelector(outletIsVisibleDeliveryLocationModalSelector)

  const [focused, setFocused] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [deliveryAddress, setDeliveryAddress] =
    useState<CustomerDeliveryAddressWithNote>(
      getInitialDeliveryAddress(initialAddress, selectedOutletAddress),
    )
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )

  const searchResultsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const scriptStatus = useScript(GoogleMapsAPIUrl)
  const { fetchGeocodeByPlaceId, fetchGeocodeResults } = useGeocodeService()

  // Fetch outlet details if not exists
  useEffect(() => {
    if (
      isOpen &&
      !isOutletDetailsPending &&
      outlet &&
      outlet.id !== outletDetailsId
    ) {
      dispatch(fetchOutletDetails({ outletId: outlet.id }))
    }
  }, [isOpen, outlet, outletDetailsId, dispatch, isOutletDetailsPending])

  // Listen to Google Maps API script status
  useEffect(() => {
    if (scriptStatus === ScriptStatus.Ready) {
      if (!window.google) {
        throw new Error('Google Maps JavaScript API library must be loaded.')
      }

      if (!window.google.maps.geometry) {
        throw new Error(
          'Google Maps Places library must be loaded. Please add `libraries=geometry` to the src URL',
        )
      }
    }
  }, [scriptStatus])

  // Attach search results view focus listener
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      const searchResultsClicked =
        searchResultsRef.current &&
        searchResultsRef.current.contains(e.target as Node)
      const searchInputClicked =
        searchInputRef.current &&
        searchInputRef.current.contains(e.target as Node)

      if (!searchInputClicked && !searchResultsClicked) {
        setFocused(false)
      }
    }

    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  // Validate delivery address bounds
  useEffect(() => {
    if (
      router.isReady &&
      deliveryAddress.location &&
      !!outletDeliveryArea?.locations?.length &&
      !!window.google &&
      !isCoordinateWithinBounds(
        deliveryAddress.location,
        outletDeliveryArea.locations,
        window.google.maps,
      )
    ) {
      setErrorMessage(
        t('components.deliveryLocationPickerModal.deliveryOutOfBounds'),
      )
    } else if (deliveryAddress.location && errorMessage) {
      setErrorMessage(undefined)
    }
  }, [
    outletDeliveryArea?.locations,
    errorMessage,
    deliveryAddress.location,
    t,
    router.isReady,
  ])

  function handleClearSearch() {
    setFocused(false)
    setSearchTerm('')
    setDeliveryAddress(
      getInitialDeliveryAddress(initialAddress, selectedOutletAddress),
    )
  }

  function handleSearchTermChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFocused(true)
    setSearchTerm(e.target.value)
  }

  function handleInputFocus() {
    setFocused(true)
  }

  function handleOnClose() {
    dispatch(outletSetIsVisibleDeliveryLocationModal(false))
  }

  function handleContinueClick() {
    const delivery = getCheckoutDeliveryDetails(
      {
        id: '',
        title: '',
        addressType: CustomerAddressType.Other,
        ...deliveryAddress,
        note: deliveryAddress.note || '',
      },
      customer,
    )
    onClickContinue(delivery)
    handleOnClose()
  }

  async function handleAddressItemClick(
    address: CustomerAddress,
    googleResult: google.maps.GeocoderResult,
  ) {
    setFocused(false)

    setDeliveryAddress({
      city: address.city,
      country: address.country,
      state: address.state,
      street: address.street,
      zipCode: address.zipCode,
      location: {
        latitude: address.location.latitude,
        longitude: address.location.longitude,
      },
      note: address.note ?? '',
    })
    setSearchTerm(googleResult.formatted_address)
  }

  async function handlePlaceSearchResultItemClick(
    placeId: string,
    description: string,
  ) {
    const geocode = await fetchGeocodeByPlaceId(placeId)
    if (geocode) {
      const addressFromGeocode = getCustomerAddressFromGeocoderResult(geocode)
      setFocused(false)
      setDeliveryAddress({
        ...deliveryAddress,
        ...addressFromGeocode,
        location: {
          // @ts-ignore - incorrect types from google.maps.GeocoderResult
          latitude:
            geocode.geometry.location.lat() || StockholmCoordinates.latitude,
          // @ts-ignore - incorrect types from google.maps.GeocoderResult
          longitude:
            geocode.geometry.location.lng() || StockholmCoordinates.longitude,
        },
      })
      setSearchTerm(description)
    }
  }

  async function handleCurrentLocationClick(
    geocodeResult: google.maps.GeocoderResult,
  ) {
    const addressFromGeocode =
      getCustomerAddressFromGeocoderResult(geocodeResult)
    setFocused(false)
    setDeliveryAddress({
      ...deliveryAddress,
      ...addressFromGeocode,
      location: {
        // @ts-ignore - incorrect types from google.maps.GeocoderResult
        latitude:
          geocodeResult.geometry.location.lat() ||
          StockholmCoordinates.latitude,
        // @ts-ignore - incorrect types from google.maps.GeocoderResult
        longitude:
          geocodeResult.geometry.location.lng() ||
          StockholmCoordinates.longitude,
      },
    })
    setSearchTerm(geocodeResult.formatted_address)
  }

  function handleModalClose() {
    handleClearSearch()
    handleOnClose()
  }

  async function handleMarkerPositionChange(
    mapProps?: IMapProps,
    map?: google.maps.Map,
    event?: { latLng: google.maps.LatLng },
  ) {
    if (!event) return

    // Leave focus from search input
    setFocused(false)

    const newMarkerPosition = {
      latitude: event.latLng.lat(),
      longitude: event.latLng.lng(),
    }

    const geocode = await fetchGeocodeResults(newMarkerPosition)

    if (geocode) {
      setSearchTerm(geocode.formatted_address)

      const address = getCustomerAddressFromGeocoderResult(geocode)
      setDeliveryAddress({
        city: address?.city || '',
        country: address?.country || '',
        state: address?.state || '',
        street: address?.street || '',
        zipCode: address?.zipCode || '',
        location: newMarkerPosition,
      })
    }
  }

  return (
    <Modal
      title={t('components.deliveryLocationPickerModal.title')}
      isOpen={isOpen}
      onClose={handleModalClose}
      mobileModalMode="full-screen"
      className={styles.modal}
    >
      <div className={styles.modalBody}>
        {!isOutletDetailsPending ? (
          <>
            <div className={styles.inputWrapper}>
              <Button
                variant="secondary"
                className={styles.backButton}
                onClick={handleOnClose}
                aria-label="go back"
              >
                <BackIcon />
              </Button>
              <Input
                leftAccessory={<SearchIcon />}
                containerClassName={styles.searchInput}
                highlighted
                placeholder={t(
                  'components.deliveryLocationPickerModal.searchLocation',
                )}
                clearable={!!searchTerm}
                dropdownView={
                  focused ? (
                    <div
                      ref={searchResultsRef}
                      className={styles.resultSection}
                    >
                      <PlacesSuggestions
                        query={searchTerm}
                        hideTitle
                        onClickAccountAddress={handleAddressItemClick}
                        onClickPlaceSearchResultItem={
                          handlePlaceSearchResultItemClick
                        }
                        onClickCurrentLocation={handleCurrentLocationClick}
                      />
                    </div>
                  ) : undefined
                }
                value={searchTerm}
                onChange={handleSearchTermChange}
                rightAccessoryOnClick={handleClearSearch}
                onFocus={handleInputFocus}
              />
            </div>
            <div className={styles.mapWrapper}>
              {scriptStatus === ScriptStatus.Ready && (
                <MapView
                  polygonPoints={outletDeliveryArea?.locations}
                  markerCoordinates={deliveryAddress.location}
                  markerConfig={{
                    draggable: true,
                    onDragend: handleMarkerPositionChange,
                  }}
                  onClickPolygon={handleMarkerPositionChange}
                />
              )}
            </div>
            <div className={styles.errorMessage}>{errorMessage}</div>
            <Button
              disabled={!!errorMessage}
              className={styles.buttonExtraStyles}
              size="large"
              onClick={handleContinueClick}
              data-testid={
                AppTestIds.DeliveryLocationPickerModal.ContinueButton
              }
            >
              {t('components.deliveryLocationPickerModal.continue')}
            </Button>
          </>
        ) : (
          <DeliveryLocationConfigureModalSkeleton />
        )}
      </div>
    </Modal>
  )
}
